import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Stack,
  alpha,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Error as ErrorIcon,
  WorkspacePremium,
  Timeline,
  Description,
  Refresh as RefreshIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { useEntity } from '../../context/EntityContext';
import { Job } from '../../types';
import {
  createBulkSession,
  uploadFilesToSession,
  startProcessing,
  getSessionStatus,
  cancelSession,
  MAX_BULK_FILES,
  BulkStatusResponse,
  BulkResumeResult
} from '../../services/bulkUploadService';

// Session storage key
const BULK_UPLOAD_SESSION_KEY = 'bulk_resume_upload_session';
const SESSION_EXPIRY_HOURS = 24;
const MAX_FILES_PER_SESSION = MAX_BULK_FILES;
const POLLING_INTERVAL_MS = 3000; // Poll every 3 seconds

interface ResumeFile {
  id: string;
  file: File;
  text?: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string;
  isPlaceholder?: boolean; // True if restored from session (needs re-upload to process)
}

interface StoredResumeFile {
  id: string;
  fileName: string;
  fileSize: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string;
}

interface MatchResult {
  id: string;
  fileName: string;
  parsedResume?: any;
  matchResult?: any;
  success: boolean;
  error?: string;
}

interface StoredSession {
  selectedJobId: string;
  files: StoredResumeFile[];
  results: MatchResult[];
  progress: number;
  timestamp: number;
}

interface DetailRowProps {
  result: MatchResult;
}

const DetailRow: React.FC<DetailRowProps> = ({ result }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  if (!result.success || !result.matchResult) {
    return null;
  }

  const { parsedResume, matchResult } = result;

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>
              {result.fileName}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{parsedResume?.name || 'N/A'}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress
              variant="determinate"
              value={matchResult.overallScore}
              size={40}
              thickness={5}
              sx={{
                color:
                  matchResult.overallScore >= 70
                    ? 'success.main'
                    : matchResult.overallScore >= 50
                    ? 'warning.main'
                    : 'error.main'
              }}
            />
            <Typography variant="h6" fontWeight={700}>
              {matchResult.overallScore}%
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            label={matchResult.confidence.toUpperCase()}
            size="small"
            color={
              matchResult.confidence === 'high'
                ? 'success'
                : matchResult.confidence === 'medium'
                ? 'warning'
                : 'default'
            }
            sx={{ fontWeight: 600 }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Detailed Analysis
              </Typography>

              {/* Summary */}
              <Card sx={{ mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Summary
                  </Typography>
                  <Typography variant="body2">{matchResult.summary}</Typography>
                </CardContent>
              </Card>

              {/* Score Breakdown */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Skills Match
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {matchResult.skillsMatch.score}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {matchResult.skillsMatch.matchedSkills.length} matched
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Experience
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="secondary.main">
                      {matchResult.experienceMatch.score}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {parsedResume?.yearsOfExperience || 'N/A'} years
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Education
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="info.main">
                      {matchResult.educationMatch.score}%
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>

              {/* Skills Details */}
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Matched Skills
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {matchResult.skillsMatch.matchedSkills.map((skill: string, idx: number) => (
                    <Chip key={idx} label={skill} size="small" color="success" variant="outlined" />
                  ))}
                </Box>
              </Box>

              {matchResult.skillsMatch.missingSkills.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Missing Skills
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {matchResult.skillsMatch.missingSkills.map((skill: string, idx: number) => (
                      <Chip key={idx} label={skill} size="small" color="error" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Strengths and Concerns */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
                <Box flex={1}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Strengths
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {matchResult.strengths.map((strength: string, idx: number) => (
                      <li key={idx}>
                        <Typography variant="body2">{strength}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
                {matchResult.concerns.length > 0 && (
                  <Box flex={1}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Concerns
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {matchResult.concerns.map((concern: string, idx: number) => (
                        <li key={idx}>
                          <Typography variant="body2" color="warning.main">
                            {concern}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Stack>

              {/* Recommendations */}
              {matchResult.recommendations.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Recommendations
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {matchResult.recommendations.map((rec: string, idx: number) => (
                      <li key={idx}>
                        <Typography variant="body2">{rec}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const BulkResumeMatchingPage: React.FC = () => {
  const theme = useTheme();
  const { entity } = useEntity();
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [resumeFiles, setResumeFiles] = useState<ResumeFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<MatchResult[]>([]);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [hasRestoredSession, setHasRestoredSession] = useState(false);

  // Backend session tracking
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [backendStatus, setBackendStatus] = useState<BulkStatusResponse | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Stop polling when component unmounts or session ends
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Session storage helpers
  const saveSession = useCallback(() => {
    try {
      const session: StoredSession = {
        selectedJobId,
        files: resumeFiles.map(f => ({
          id: f.id,
          fileName: f.file.name,
          fileSize: f.file.size,
          status: f.status,
          error: f.error
        })),
        results,
        progress,
        timestamp: Date.now()
      };
      // Also save backend session ID if exists
      if (sessionId) {
        (session as any).backendSessionId = sessionId;
      }
      localStorage.setItem(BULK_UPLOAD_SESSION_KEY, JSON.stringify(session));
    } catch (err) {
      console.error('Failed to save session:', err);
    }
  }, [selectedJobId, resumeFiles, results, progress, sessionId]);

  const loadSession = useCallback((): StoredSession | null => {
    try {
      const stored = localStorage.getItem(BULK_UPLOAD_SESSION_KEY);
      if (!stored) return null;

      const session: StoredSession = JSON.parse(stored);

      // Check if session expired
      const hoursOld = (Date.now() - session.timestamp) / (1000 * 60 * 60);
      if (hoursOld > SESSION_EXPIRY_HOURS) {
        localStorage.removeItem(BULK_UPLOAD_SESSION_KEY);
        return null;
      }

      return session;
    } catch (err) {
      console.error('Failed to load session:', err);
      return null;
    }
  }, []);

  const clearSession = useCallback(async () => {
    // Cancel any active backend session
    if (sessionId) {
      try {
        await cancelSession(sessionId);
      } catch (err) {
        console.error('Failed to cancel session:', err);
      }
    }
    stopPolling();
    localStorage.removeItem(BULK_UPLOAD_SESSION_KEY);
    setSelectedJobId('');
    setResumeFiles([]);
    setResults([]);
    setProgress(0);
    setErrorMessage('');
    setSuccessMessage('');
    setSessionId(null);
    setBackendStatus(null);
  }, [sessionId, stopPolling]);

  // Save session when state changes (after initial load)
  useEffect(() => {
    if (hasRestoredSession && (resumeFiles.length > 0 || results.length > 0)) {
      saveSession();
    }
  }, [selectedJobId, resumeFiles, results, progress, hasRestoredSession, saveSession]);

  // Fetch logged-in user and their jobs
  useEffect(() => {
    loadUserAndJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id]);

  // Convert backend resume results to frontend MatchResult format
  const convertBackendResults = (resumes: BulkResumeResult[]): MatchResult[] => {
    return resumes
      .filter(r => r.status === 'completed' || r.status === 'failed')
      .map(r => ({
        id: r.id,
        fileName: r.fileName,
        parsedResume: r.parsedData,
        matchResult: r.matchResult,
        success: r.status === 'completed',
        error: r.error
      }))
      .sort((a, b) => {
        const scoreA = a.matchResult?.overallScore || 0;
        const scoreB = b.matchResult?.overallScore || 0;
        return scoreB - scoreA;
      });
  };

  // Poll backend for session status
  const pollSessionStatus = useCallback(async (sid: string) => {
    try {
      const status = await getSessionStatus(sid);
      setBackendStatus(status);
      setProgress(status.session.progress);

      // Convert backend results to frontend format
      const matchResults = convertBackendResults(status.resumes);
      setResults(matchResults);

      // Update file statuses
      setResumeFiles(prev => prev.map(f => {
        const backendResume = status.resumes.find(r => r.fileName === f.file.name);
        if (backendResume) {
          return {
            ...f,
            status: backendResume.status === 'completed' ? 'completed' :
                   backendResume.status === 'failed' ? 'error' :
                   backendResume.status === 'processing' ? 'processing' : 'pending',
            error: backendResume.error
          };
        }
        return f;
      }));

      // Check if processing is complete
      if (status.session.status === 'completed' || status.session.status === 'failed' || status.session.status === 'cancelled') {
        stopPolling();
        setProcessing(false);

        if (status.session.status === 'completed') {
          const successCount = status.session.successfulFiles;
          const failedCount = status.session.failedFiles;
          setSuccessMessage(`Processing complete! ${successCount} resume(s) matched successfully${failedCount > 0 ? `, ${failedCount} failed` : ''}.`);
        } else if (status.session.status === 'failed') {
          setErrorMessage('Processing failed. Please try again.');
        }
      }
    } catch (err: any) {
      console.error('Error polling session status:', err);
      // Don't stop polling on transient errors, but log them
    }
  }, [stopPolling]);

  // Start polling for a session
  const startPolling = useCallback((sid: string) => {
    stopPolling(); // Clear any existing polling
    setProcessing(true);
    pollingIntervalRef.current = setInterval(() => {
      pollSessionStatus(sid);
    }, POLLING_INTERVAL_MS);
    // Also poll immediately
    pollSessionStatus(sid);
  }, [stopPolling, pollSessionStatus]);

  const loadUserAndJobs = async () => {
    try {
      setJobsLoading(true);

      // Get logged-in user
      const user = await getAuthUser();
      if (!user) {
        setErrorMessage('You must be logged in to access this page');
        setJobsLoading(false);
        return;
      }

      setCurrentUser(user);

      // Fetch jobs for the user's entity (only active jobs)
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('entity_id', user.entityId)
        .eq('status', 'active')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setJobs(data || []);

      // Restore session after jobs are loaded
      const session = loadSession();
      if (session) {
        setSelectedJobId(session.selectedJobId);

        // Check if there's a backend session ID to restore
        const backendSessionId = (session as any).backendSessionId;
        if (backendSessionId) {
          try {
            // Fetch status from backend
            const status = await getSessionStatus(backendSessionId);
            setSessionId(backendSessionId);
            setBackendStatus(status);
            setProgress(status.session.progress);

            // Convert backend results to frontend format
            const matchResults = convertBackendResults(status.resumes);
            setResults(matchResults);

            // Restore file list from backend
            const restoredFiles: ResumeFile[] = status.resumes.map(r => ({
              id: r.id,
              file: new File([], r.fileName, { type: 'application/pdf' }),
              status: r.status === 'completed' ? 'completed' :
                     r.status === 'failed' ? 'error' :
                     r.status === 'processing' ? 'processing' : 'pending',
              error: r.error,
              isPlaceholder: true
            }));
            setResumeFiles(restoredFiles);

            // If session is still processing, start polling
            if (status.session.status === 'processing' || status.session.status === 'pending') {
              startPolling(backendSessionId);
              setSuccessMessage('Resuming previous session... Processing continues in background.');
            } else if (status.session.status === 'completed') {
              setSuccessMessage(`Previous session restored. ${status.session.successfulFiles} resume(s) processed.`);
            }
          } catch (backendErr: any) {
            console.error('Failed to restore backend session:', backendErr);
            // Fall back to local session data if backend fails
            if (session.results.length > 0) {
              setResults(session.results);
              setProgress(session.progress);
              setSuccessMessage('Previous results restored (session expired on server).');
            }
          }
        } else if (session.results.length > 0 || session.files.length > 0) {
          // No backend session, use local data
          setResults(session.results);
          setProgress(session.progress);

          const completedFileIds = new Set(session.results.map(r => r.id));
          const restoredFiles: ResumeFile[] = session.files
            .filter(f => completedFileIds.has(f.id) || f.status === 'completed')
            .map(f => ({
              id: f.id,
              file: new File([], f.fileName, { type: 'application/pdf' }),
              status: f.status,
              error: f.error,
              isPlaceholder: true
            }));
          setResumeFiles(restoredFiles);

          if (session.results.length > 0) {
            setSuccessMessage('Previous session restored. Results are shown below.');
          }
        }
      }
      setHasRestoredSession(true);
    } catch (err: any) {
      console.error('Error loading jobs:', err);
      setErrorMessage('Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Check file limit
    const currentCount = resumeFiles.filter(f => !f.isPlaceholder).length;
    const newCount = currentCount + acceptedFiles.length;

    if (newCount > MAX_FILES_PER_SESSION) {
      setErrorMessage(`Maximum ${MAX_FILES_PER_SESSION} files allowed per session. You have ${currentCount} files and are trying to add ${acceptedFiles.length} more.`);
      return;
    }

    const newFiles: ResumeFile[] = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substring(7),
      file,
      status: 'pending'
    }));
    setResumeFiles(prev => [...prev, ...newFiles]);
    setErrorMessage('');
  }, [resumeFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: true
  });

  const removeFile = (id: string) => {
    setResumeFiles(prev => prev.filter(f => f.id !== id));
  };

  const processResumes = async () => {
    if (!selectedJobId) {
      setErrorMessage('Please select a job first');
      return;
    }

    const filesToProcess = resumeFiles.filter(f => !f.isPlaceholder);
    if (filesToProcess.length === 0) {
      setErrorMessage('Please upload at least one resume');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setSuccessMessage('');
    setResults([]);
    setProgress(0);

    try {
      if (!currentUser?.entityId) {
        throw new Error('User entity not found');
      }

      // Step 1: Create backend session
      setSuccessMessage('Creating processing session...');
      const { sessionId: newSessionId, isExisting } = await createBulkSession(selectedJobId);
      setSessionId(newSessionId);

      if (isExisting) {
        setSuccessMessage('Resuming existing session...');
      }

      // Step 2: Upload files to storage and add to session
      setSuccessMessage('Uploading files...');
      const filesToUpload = filesToProcess.map(f => f.file);

      await uploadFilesToSession(
        newSessionId,
        filesToUpload,
        currentUser.entityId,
        (uploaded, total) => {
          setProgress((uploaded / total) * 20); // Upload is 20% of progress
        }
      );

      setUploading(false);

      // Step 3: Start backend processing
      setSuccessMessage('Starting AI processing... You can navigate away and come back to check progress.');
      await startProcessing(newSessionId);

      // Step 4: Start polling for status updates
      startPolling(newSessionId);

    } catch (error: any) {
      console.error('Error starting bulk upload:', error);
      setErrorMessage(error.message || 'Failed to start processing');
      setUploading(false);
      setProcessing(false);
    }
  };

  // Cancel processing
  const handleCancelProcessing = async () => {
    if (sessionId) {
      try {
        await cancelSession(sessionId);
        stopPolling();
        setProcessing(false);
        setSuccessMessage('Processing cancelled.');
      } catch (err: any) {
        console.error('Failed to cancel:', err);
        setErrorMessage('Failed to cancel processing');
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'text.primary'
            }}
          >
            Bulk Resume Matching
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Upload multiple resumes and match them against a job description using AI
          </Typography>
        </Box>
        {(resumeFiles.length > 0 || results.length > 0) && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={clearSession}
            disabled={processing}
            sx={{ flexShrink: 0 }}
          >
            Start New Session
          </Button>
        )}
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Job Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Step 1: Select Job Position
          </Typography>
          <FormControl fullWidth disabled={processing}>
            <InputLabel>Select Job</InputLabel>
            <Select
              value={selectedJobId}
              onChange={(e) => setSelectedJobId(e.target.value)}
              label="Select Job"
            >
              {jobsLoading ? (
                <MenuItem disabled>Loading jobs...</MenuItem>
              ) : jobs.length === 0 ? (
                <MenuItem disabled>No jobs available</MenuItem>
              ) : (
                jobs.map((job: Job) => (
                  <MenuItem key={job.id} value={job.id}>
                    {job.title} - {job.location}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Resume Upload */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Step 2: Upload Resumes
          </Typography>
          <Box
            {...getRootProps()}
            sx={{
              border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                borderColor: theme.palette.primary.main
              }
            }}
          >
            <input {...getInputProps()} disabled={processing} />
            <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? 'Drop files here' : 'Drag & drop resume files here'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse (PDF, DOC, DOCX)
            </Typography>
          </Box>

          {resumeFiles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Files ({resumeFiles.length})
              </Typography>
              <Stack spacing={1}>
                {resumeFiles.map(file => (
                  <Box
                    key={file.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      bgcolor: file.status === 'completed' ? alpha(theme.palette.success.main, 0.05) :
                               file.status === 'error' ? alpha(theme.palette.error.main, 0.05) :
                               file.status === 'processing' ? alpha(theme.palette.info.main, 0.05) :
                               alpha(theme.palette.primary.main, 0.03),
                      borderRadius: 1,
                      border: `1px solid ${
                        file.status === 'completed' ? alpha(theme.palette.success.main, 0.2) :
                        file.status === 'error' ? alpha(theme.palette.error.main, 0.2) :
                        file.status === 'processing' ? alpha(theme.palette.info.main, 0.2) :
                        alpha(theme.palette.divider, 0.1)
                      }`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {file.status === 'processing' ? (
                        <CircularProgress size={20} />
                      ) : file.status === 'completed' ? (
                        <Chip label="Done" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                      ) : file.status === 'error' ? (
                        <Chip label="Failed" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                      ) : (
                        <Description color="action" />
                      )}
                      <Typography variant="body2">{file.file.name}</Typography>
                      {!file.isPlaceholder && (
                        <Typography variant="caption" color="text.secondary">
                          ({(file.file.size / 1024).toFixed(1)} KB)
                        </Typography>
                      )}
                      {file.isPlaceholder && (
                        <Chip label="Restored" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    {!file.isPlaceholder && !processing && (
                      <IconButton
                        size="small"
                        onClick={() => removeFile(file.id)}
                        disabled={processing || uploading}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Process Button */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={processResumes}
            disabled={processing || uploading || !selectedJobId || resumeFiles.filter(f => !f.isPlaceholder).length === 0}
            startIcon={(processing || uploading) ? <CircularProgress size={20} /> : <WorkspacePremium />}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1rem',
              color: '#ffffff'
            }}
          >
            {uploading ? 'Uploading...' : processing ? 'Processing...' : 'Start AI Matching'}
          </Button>
          {processing && (
            <Button
              variant="outlined"
              size="large"
              onClick={handleCancelProcessing}
              color="error"
              startIcon={<PauseIcon />}
              sx={{
                px: 3,
                py: 1.5,
                fontWeight: 600
              }}
            >
              Cancel
            </Button>
          )}
        </Stack>
        {!processing && !uploading && resumeFiles.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {resumeFiles.filter(f => !f.isPlaceholder).length} file(s) ready to process (max {MAX_FILES_PER_SESSION})
          </Typography>
        )}
      </Box>

      {/* Progress */}
      {(processing || uploading) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">
                {uploading ? 'Uploading files...' : 'Processing resumes...'} {Math.round(progress)}%
              </Typography>
              {backendStatus && (
                <Typography variant="caption" color="text.secondary">
                  {backendStatus.session.processedFiles} / {backendStatus.session.totalFiles} completed
                </Typography>
              )}
            </Box>
            <LinearProgress variant="determinate" value={progress} />
            {processing && !uploading && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                You can navigate away from this page. Processing will continue in the background.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Timeline color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Matching Results (Sorted by Best Match)
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50} />
                    <TableCell>File Name</TableCell>
                    <TableCell>Candidate Name</TableCell>
                    <TableCell>Match Score</TableCell>
                    <TableCell>Confidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(result =>
                    result.success ? (
                      <DetailRow key={result.id} result={result} />
                    ) : (
                      <TableRow key={result.id}>
                        <TableCell colSpan={5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon color="error" />
                            <Typography variant="body2">
                              {result.fileName}: {result.error || 'Processing failed'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
